import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useWithGA from './useWithGA';
import useStrapiPageData from './data/useStrapiPageData';
import useConvertRegionToShort from './useConvertRegionToShort';
import useGetCategoryData from './data/useGetCategoryData';
import useFetchAllCategories from './data/useFetchAllCategories';

import { Graph } from '../../static/icons/graph';
import { Hands } from '../../static/icons/hands';
import { Tasks } from '../../static/icons/tasks';
import { Touch } from '../../static/icons/touch';
import { Robot } from '../../static/icons/robot';

import BusinesswithReviewForm from '../components/BusinesswithReviewForm';

import CONSTANTS_NUMBERS from '../constants/numbers';

import {
  PAGE_FOR_VENDOR,
  PAGE_SYSTEMGUIDE,
  PAGE_SYSTEM,
  PAGE_CATEGORIES,
  PAGE_SYSTEM_ALTERNATIVE,
  PAGE_LEGAL_PRIVACY,
  PAGE_LEGAL_TERMS,
} from '../constants/strapi';

import {
  isCustomer,
  isCustomerOrPost,
  getSizeRange,
  createUrlPath,
} from '../utils/utils';
import {
  getAverageReviewsRatings,
  groupReviewQuestionsBySpecialNames,
} from '../utils/reviews';

import {
  unEscapeHtml,
  getLogoAssetObject,
  getPitchAssets,
  convertLanguageToShort,
  getBannerAssetObject,
} from '../utils/sharedFunc';

import {
  isLocaleSweden,
  isLocaleNorway,
  isLocaleDenmark,
  isLocaleNordics,
} from '../utils/locale';

const useSystemPage = (
  data,
  locale,
  systemsToCompareData,
  { createInquiryFn, createInquiryData, createInquiryLoading },
  { preview = false } = {}
) => {
  const { handleDataLayer } = useWithGA();
  const { t } = useTranslation();
  const convertRegionToShort = useConvertRegionToShort();

  const isSweden = isLocaleSweden(locale);
  const isNordics = isLocaleNordics(locale);

  const {
    system,
    categoriesSystemProps,
    sameCompanySystems,
    comparedSystems,
    systemReviews,
  } = data;

  const { allCategories } = useFetchAllCategories();

  const { categoryName, categoryUrl, categoryHasGuide } = useGetCategoryData(
    system?.defaultCategory
  );

  const [forVendorPageUrl] = useStrapiPageData(PAGE_FOR_VENDOR);
  const [systemguidePageUrl] = useStrapiPageData(PAGE_SYSTEMGUIDE);
  const [systemPageUrl] = useStrapiPageData(PAGE_SYSTEM);
  const [categoriesPageUrl] = useStrapiPageData(PAGE_CATEGORIES);
  const [systemAlternativesPageUrl] = useStrapiPageData(
    PAGE_SYSTEM_ALTERNATIVE
  );
  const [legalPrivacyPageUrl] = useStrapiPageData(PAGE_LEGAL_PRIVACY);
  const [legalTermsPageUrl] = useStrapiPageData(PAGE_LEGAL_TERMS);

  const {
    id: systemId,
    ratingBW,
    systemSiteDataForLocale,
    categories,
    defaultCategory: category,
  } = system || {};

  const {
    systemName,
    introDescription,
    introDescriptionWhat,
    introDescriptionWho,
    systemDescription,
    companyDescription,
    marketPositionDescription,
    whySystemDescription,
    whyCompanyDescription,
    solvesClientDescription,
    systemExternalUrl,
    customerType,
    companyName,
    mediaAssets,
    locale: siteDataLocale,
  } = systemSiteDataForLocale || {};

  const logoAsset = getLogoAssetObject(mediaAssets);
  const pitchAsset = getPitchAssets(mediaAssets);
  // This is used to determine the locale of the banner asset to properly handle fallback logic
  const bannerAsset = getBannerAssetObject(
    data?.system?.mediaAssets,
    siteDataLocale || locale
  );
  
  let sameCompanySystemsFix = sameCompanySystems;
  if (sameCompanySystems.length > 4) {
    sameCompanySystemsFix = sameCompanySystems.slice(0, 4);
  }

  const customer = isCustomer(customerType);

  const customerOrPost = isCustomerOrPost(customerType);
  const notCustomerOrPostInDkNo =
    !customerOrPost && (isLocaleNorway(locale) || isLocaleDenmark(locale));

  const hasReviews = !!systemReviews?.length;
  const introText = systemDescription || introDescription;
  const styledIntroText = !!systemDescription?.length;
  const hasFaqTexts =
    !!introDescriptionWhat?.length || !!introDescriptionWho?.length;

  const textSections = useMemo(() => {
    if (styledIntroText) {
      return {
        length: unEscapeHtml(introText || 0).length,
        texts: [
          {
            id: 'text1',
            title: t('sp.descriptionTitle', { systemName }),
            text: unEscapeHtml(introText || 0),
            styledText: styledIntroText,
          },
        ],
      };
    }
    if (hasFaqTexts) {
      return {
        length:
          unEscapeHtml(introDescriptionWhat || '').length +
          unEscapeHtml(introDescriptionWho || '').length,
        texts: [
          {
            id: 'text2',
            title: t('sp.descriptionTitle', { systemName }),
            text: unEscapeHtml(introDescriptionWhat || ''),
            styledText: true,
          },
          {
            id: 'text3',
            title: t('sp.descriptionTitle2', { systemName }),
            text: unEscapeHtml(introDescriptionWho || ''),
            styledText: true,
          },
        ],
      };
    }
    return {
      length: unEscapeHtml(introText || 0).length,
      texts: [
        {
          id: 'text1',
          title: t('sp.descriptionTitle', { systemName }),
          text: unEscapeHtml(introText || 0),
          styledText: styledIntroText,
        },
      ],
    };
  }, [introText, styledIntroText, hasFaqTexts]);

  const reviewData = useMemo(
    () =>
      systemReviews?.map((review) => {
        const { originalLocale, availableLocales } = review;

        const isTranslated =
          availableLocales.includes(locale) && locale !== originalLocale;
        const reviewDate = new Date(review.createdAt).toISOString();
        const year = reviewDate.substring(0, 4);
        const month = reviewDate.substring(5, 7);

        let standardQuestions = [];
        let ratingQuestions = [];

        review.questions.forEach((question) => {
          if (question.questionType === 'RATING') {
            ratingQuestions.push(question);
          } else {
            standardQuestions.push(question);
          }
        });

        const groupedQuestions = groupReviewQuestionsBySpecialNames(
          standardQuestions,
          ['USER_ROLE', 'COMPANY_SIZE']
        );

        standardQuestions = groupedQuestions.OTHER.map((question) => ({
          question:
            question.questionText[locale] ||
            question.questionText[originalLocale],
          answer: question.answerText[originalLocale] || '-',
          translatedAnswer: isTranslated && question.answerText[locale],
        }));

        ratingQuestions = ratingQuestions.map((question) => ({
          text:
            question.questionText[locale] ||
            question.questionText[originalLocale],
          rating: question.answerText,
        }));

        const userRole = isTranslated
          ? groupedQuestions.USER_ROLE?.[0]?.answerText?.[locale]
          : groupedQuestions.USER_ROLE?.[0]?.answerText?.[originalLocale];

        const companySize = isTranslated
          ? groupedQuestions.COMPANY_SIZE?.[0]?.answerText?.[locale]
          : groupedQuestions.COMPANY_SIZE?.[0]?.answerText?.[originalLocale];

        const tags = [
          { text: `${companySize}` },
          {
            text: t('sp.reviewCardVerified'),
            icon:
              review?.verification === 'LINKEDIN' ? review.verification : null,
          },
          {
            text: t(`shared.localeToCountry.${review.originalLocale}`),
          },
          { text: `${t(`month.${month}`)} ${year}` },
        ];

        if (userRole) {
          tags.unshift({ text: userRole });
        }

        const reviewObj = {
          id: review.id,
          title: review.title[originalLocale],
          translatedTitle: isTranslated && review.title[locale],
          commentText: review.commentText[originalLocale],
          commentAuthor: review.commentAuthor[originalLocale],
          translatedCommentText:
            isTranslated && (review.commentText[locale] || ''),
          translatedCommentAuthor:
            isTranslated && (review.commentAuthor[locale] || ''),
          isTranslated,
          tags,
          questions: standardQuestions,
          ratings: ratingQuestions,
        };

        return reviewObj;
      }),
    [systemReviews]
  );

  const systemPropsArr = useMemo(() => {
    const productFuncFields = categoriesSystemProps.reduce((acc, curr) => {
      const { dbName, category: currCategory } = curr;

      const updatedObj = { ...acc };
      if (acc[currCategory]) {
        updatedObj[currCategory].push(dbName);
      } else {
        updatedObj[currCategory] = [];
        updatedObj[currCategory].push(dbName);
      }
      return updatedObj;
    }, {});

    const systemPropsPerCategory =
      system?.systemProps &&
      Object.keys(system.systemProps)
        .filter((catKey) => categories?.includes(catKey))
        .map((catKey) => {
          const catName = allCategories[catKey].categoryName;

          const systemPropsCat = system.systemProps[catKey];
          const possibleFields = Object.keys(systemPropsCat)
            .filter((itemKey) => productFuncFields[catKey]?.includes(itemKey))
            .filter(
              (fieldKey) =>
                systemPropsCat[fieldKey] &&
                (systemPropsCat[fieldKey] === true ||
                  systemPropsCat[fieldKey]?.length)
            );

          const items = possibleFields
            .map((propKey) => {
              const propValue = systemPropsCat[propKey];
              return {
                id: propKey,
                text: t([
                  `prodFunc.${catKey}.${propKey}`,
                  `prodFunc.common.${propKey}`,
                ]),
                value: propValue,
              };
            })
            .filter(
              (item) => item?.value && ['ADD', 'YES'].includes(item?.value)
            )
            .filter((item) => !!item.text)
            .sort((a, b) => {
              const nameA = a.text.toUpperCase();
              const nameB = b.text.toUpperCase();
              if (nameA > nameB) {
                return 1;
              }
              if (nameA < nameB) {
                return -1;
              }
              return 0;
            });

          return {
            title: catName,
            items,
          };
        });

    return systemPropsPerCategory || [];
  }, [system?.systemProps]);

  const uspItems = useMemo(() => {
    const iconSize = 100;
    const items = [];
    if (whySystemDescription?.length) {
      items.push({
        id: 'whyChooseSystem',
        iconImg: <Hands width={iconSize} height={iconSize} />,
        title: t('sp.whyChooseSystem', { systemName }),
        text: unEscapeHtml(whySystemDescription || ''),
      });
    }
    if (whyCompanyDescription?.length) {
      items.push({
        id: 'whyChooseSupplier',
        iconImg: <Touch width={iconSize} height={iconSize} />,
        title: t('sp.whyChooseSupplier', { companyName }),
        text: unEscapeHtml(whyCompanyDescription || ''),
      });
    }
    if (marketPositionDescription?.length) {
      items.push({
        id: 'whyChooseMarketPosition',
        iconImg: <Tasks width={iconSize} height={iconSize} />,
        title: t('sp.whyChooseMarketPosition'),
        text: unEscapeHtml(marketPositionDescription || ''),
      });
    }
    if (solvesClientDescription?.length) {
      items.push({
        id: 'whyChooseNeedsChallanges',
        iconImg: <Graph width={iconSize} height={iconSize} />,
        title: t('sp.whyChooseNeedsChallanges', { systemName }),
        text: unEscapeHtml(solvesClientDescription || ''),
      });
    }

    return items;
  }, [
    whySystemDescription,
    whyCompanyDescription,
    marketPositionDescription,
    solvesClientDescription,
  ]);

  if (!system) {
    return null;
  }

  const saveInquiry = (formData) => {
    const { referrer } = window.frames.top.document;

    const updatedFormData = {
      category: category || null,
      system: systemId || null,
      textField: formData.text,
      url: document.location.href,
      type: formData.formType,
      firstName: formData.first_name,
      lastName: formData.last_name,
      company: formData.company,
      email: formData.email,
      phone: formData.phone,
      acceptAgree: true,
      locale,
      inquiryType: formData.type,
      licenses: formData.license_number || null,
      referrer,
      sessionId: sessionStorage.getItem('sessionId') || null,
    };

    createInquiryFn({ variables: { data: JSON.stringify(updatedFormData) } });

    const dlObj = {
      event_name: 'form_submit',
      form_name: 'Contact system supplier',
      page_type: 'System page',
      system_name: systemName,
      system_category: category,
    };

    handleDataLayer(dlObj);
  };

  let urlToGuide = systemguidePageUrl;

  if (categoryHasGuide) {
    urlToGuide = `/${categoryUrl}${systemguidePageUrl}`;
  }

  const existInCat = categories
    .map((cat) => {
      const catName = allCategories[cat]?.categoryName;
      const catUrl = allCategories[cat]?.slugi18n;

      if (!catName || !catUrl) {
        return null;
      }

      return {
        name: catName,
        url: `/${catUrl}/`,
        disabled: preview,
      };
    })
    .filter((cat) => cat);

  const averageRatings =
    hasReviews && getAverageReviewsRatings(systemReviews, locale);

  const systemCompareData = comparedSystems.map((compareObj) => {
    const {
      count,
      id: compareSystemId,
      systemSiteDataForLocale: comparedLocalData,
    } = compareObj;

    const { mediaAssets: compareMediaAssets } = comparedLocalData;

    const systemUrl = systemsToCompareData.find(
      (obj) => obj.systemId === compareSystemId
    ).path;

    const topLogoAsset = getLogoAssetObject(compareMediaAssets);
    const bottomLogoAsset = getLogoAssetObject(mediaAssets);

    return {
      id: `${systemId}-${compareSystemId}`,
      count,
      compareUrl: systemUrl,
      systems: [
        {
          id: compareSystemId,
          name: comparedLocalData.systemName,
          logoUrl: `https://ik.imagekit.io/businesswith/tr:w-80,h-40,cm-pad_resize,dpr-2/logo/${topLogoAsset.url}`,
        },
        {
          id: systemId,
          name: systemName,
          logoUrl: `https://ik.imagekit.io/businesswith/tr:w-80,h-40,cm-pad_resize,dpr-2/logo/${bottomLogoAsset.url}`,
        },
      ],
    };
  });

  const alternativesUrl = createUrlPath([
    systemPageUrl,
    systemSiteDataForLocale.systemNameUrl,
    systemAlternativesPageUrl,
  ]);

  const breadcrumbs = [
    {
      title: t('common.home'),
      href: '/',
      disabled: preview,
    },
    { title: categoryName, href: `/${categoryUrl}/`, disabled: preview },
    {
      title: systemName,
      href: null,
      disabled: preview,
    },
  ];

  const quickFacts = [
    {
      title: t('spQf.clientCompanySize'),
      description: t('spQf.clientCompanySizeHelp'),
      type: 'textOptions',
      value: getSizeRange(system.clientCompanySize),
      unpaid: true,
    },
    {
      title: t('spQf.mobileApp'),
      description: t('spQf.mobileAppHelp'),
      type: 'iconsMobile',
      value: [
        system.mobileApps.includes('IOS'),
        system.mobileApps.includes('ANDROID'),
        system.mobileApps.includes('RESPONSIVE'),
      ],
      unpaid: false,
    },
    {
      title: t('spQf.support'),
      description: t('spQf.supportHelp'),
      type: 'iconsSupport',
      value: [
        system.support.includes('WEB') || system.support.includes('WEBADD'),
        system.support.includes('PHONE') || system.support.includes('PHONEADD'),
        system.support.includes('EMAIL') || system.support.includes('EMAILADD'),
      ],
      unpaid: true,
    },
    {
      title: t('spQf.education'),
      description: t('spQf.educationHelp'),
      type: 'iconsEducation',
      value: [
        system.education.includes('WEBINAR'),
        system.education.includes('DOCUMENT'),
        system.education.includes('ONSITEATCUSTOMER'),
      ],
      unpaid: false,
    },
    {
      title: t('spQf.freeTrial'),
      description: t('spQf.freeTrialHelp'),
      type: 'boolean',
      value: system.freeTrial,
      unpaid: true,
    },
    {
      title: t('spQf.price'),
      description: t('spQf.priceHelp'),
      type: 'text',
      value: systemSiteDataForLocale.price,
      unpaid: true,
    },
  ];

  if (isNordics) {
    quickFacts.push({
      title: t('spQf.languages'),
      description: t('spQf.languagesHelp'),
      type: 'options',
      value: system.languages.map((language) => ({
        text: convertLanguageToShort(language),
        hoverText: `constants.${language.toLowerCase()}`,
      })),
      unpaid: true,
    });
    quickFacts.push({
      title: t('spQf.marketRegions'),
      description: t('spQf.marketRegionsHelp'),
      type: 'options',
      value: system.marketRegions.map((region) => ({
        text: convertRegionToShort(region),
        hoverText: `constants.${region.toLowerCase()}`,
      })),
      unpaid: true,
    });
  }

  if (sameCompanySystemsFix) {
    quickFacts.push({
      title: t('spQf.sameCompanySystems'),
      description: t('spQf.sameCompanySystemsHelp'),
      type: 'link',
      value: sameCompanySystemsFix?.map((sameSystem) => {
        const { systemSiteDataForLocale: sameLocaleData, id: sameId } =
          sameSystem;
        const { systemName: sameSystemName, systemNameUrl: sameSystemUrl } =
          sameLocaleData;
        return {
          id: sameId,
          name: sameSystemName,
          url: `${systemPageUrl}${sameSystemUrl}/`,
        };
      }),
      unpaid: false,
    });
  }

  const sections = [
    {
      sectionType: 'single',
      component: {
        type: 'header',
        componentData: {
          title: systemSiteDataForLocale.systemName,
          subTitle: systemSiteDataForLocale.companyName,
          rating: ratingBW,
          imgUrl: `https://ik.imagekit.io/businesswith/tr:w-200,h-100,cm-pad_resize,dpr-2/logo/${logoAsset.url}`,
          bannerUrl: bannerAsset?.url
            ? `https://ik.imagekit.io/businesswith/tr:w-1250,h-300,dpr-2/banner/${bannerAsset.url}`
            : null,
          bannerConfig: bannerAsset?.config,
          hasBanner: customer && !!bannerAsset?.url,
          reviewButtonHref: '/#',
          websiteButtonHref: systemSiteDataForLocale.systemExternalUrl,
          customerType: systemSiteDataForLocale.customerType,
          followLink: systemId === 'ckfjrz4yc01ig0744aufjiiyq',
          extraBtnText: systemSiteDataForLocale.extraBtnText,
          extraBtnLink: systemSiteDataForLocale.extraBtnLink,
          card: {
            title: isSweden
              ? t('common.helpSystemGuide')
              : t('common.trySystemGuide'),
            description: t('common.systemGuideAlgorithmsText', {
              number: CONSTANTS_NUMBERS.guideRuns,
            }),
            url: urlToGuide,
            icon: <Robot color="#297FFF" width="5" />,
            handleClick: () => {
              handleDataLayer({
                event_name: 'to_systemguide',
                page_type: 'System page',
                system_category: category || '',
              });
              // TODO: Implement this all the way
              // handleDataLayer({
              //   event_name: 'cta',
              //   page_type: 'Category page',
              //   system_category: category || '',
              //   type: 'systemguide',
              // });
            },
          },
          companyName,
          systemName: systemSiteDataForLocale.systemName,
          alternativesUrl,
          systemId,
          category: system.defaultCategory,
          categoryName,
        },
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'infoSection',
        textSections,
        form: {
          btn: t('formCommon.priceDemo'),
          // toggleForm: () => {
          //   console.log('toggle form');
          // },
        },
        pitchAsset,
        category,
        systemName,
        showForm: customer || notCustomerOrPostInDkNo,
        componentData: quickFacts,
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'media',
        componentData: systemSiteDataForLocale.mediaAssets.filter(
          (asset) =>
            asset.type !== 'PITCH' &&
            asset.type !== 'LOGO' &&
            asset.type !== 'BANNER'
        ),
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'sectionUSP',
        title: t('sp.whyChooseTitle', { companyName }),
        buttonText: t('formInfoDemoOffer.offerBtn'),
        componentData: uspItems,
      },
    },
    {
      sectionType: 'dubble',
      type: 'reviews',
      components: [
        {
          type: 'general',
          shortTitle: t('sp.reviewsDescription'),
          title: t('sp.reviewsTitle', { systemName }),
          componentData: averageRatings,
          hasReviews,
        },
        {
          type: 'ratings',
          titleIcon: null,
          title: null,
          componentData: reviewData,
          hasReviews,
        },
      ],
    },
    {
      sectionType: 'single',
      component: {
        type: 'companyCheck',
        componentData: {
          title: t('sp.workForCompany'),
          text: t('sp.readAboutUpdateInfo'),
          buttonText: t('sp.readAboutUpdateInfo'),
          buttonHref: forVendorPageUrl,
        },
      },
    },
    // {
    //   sectionType: 'single',
    //   component: {
    //     type: 'alternatives',
    //     title: t('sp.alternativesTitle', { systemName }),
    //     buttonText: 'TODO: Fler alternativ till {systemName}',
    //     buttonHref: `/${categoryUrl}/`,
    //     componentData: [
    //       {
    //         id: '1',
    //         name: 'TODO: Single line name',
    //         imgUrl: 'https://picsum.photos/60/60',
    //         rating: 4,
    //         description: 'TODO: Lär dig mer om QBIS Project',
    //         compareHref: '/#',
    //       },
    //       {
    //         id: '2',
    //         name: 'TODO: Single line name',
    //         imgUrl: 'https://picsum.photos/60/60',
    //         rating: 4,
    //         description: 'TODO: Lär dig mer om QBIS Project',
    //         compareHref: '/#',
    //       },
    //       {
    //         id: '3',
    //         name: 'Single line name',
    //         imgUrl: 'https://picsum.photos/60/60',
    //         rating: 4,
    //         description: 'Lär dig mer om QBIS Project',
    //         compareHref: '/#',
    //       },
    //     ],
    //   },
    // },
    {
      sectionType: 'single',
      component: {
        type: 'doYouKnow',
        componentData: {
          title: t('sp.noReviewKnowSystem', { systemName }),
          text: t('sp.noReviewKnowSystemText'),
          buttonText: t('sp.writeReview'),
          buttonHref: '/#',
        },
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'popularCategories',
        title: t('sp.systemExistInCats', { systemName }),
        linkUrl: categoriesPageUrl,
        componentData: existInCat,
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'accordionSection',
        title: t('sp.productFunctionalities', { systemName }),
        componentData: systemPropsArr,
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'systemRightForMe',
        componentData: {
          title: t('guide.doesSystemFitsMe'),
          text: t('common.systemGuideAlgorithmsText', {
            number: CONSTANTS_NUMBERS.guideRuns,
          }),
          buttonText: t('common.trySystemGuide'),
          buttonHref: urlToGuide,
        },
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'aboutSection',
        componentData: {
          title: t('sp.aboutCompany', { companyName }),
          description: unEscapeHtml(companyDescription),
          imgUrl: `https://ik.imagekit.io/businesswith/tr:w-150,h-150,cm-pad_resize,dpr-2/logo/${logoAsset.url}`,
          imgAlt: t('sp.aboutCompany', { companyName }),
        },
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'compare',
        title: t('sp.commonComparisonInCat', { category: categoryName }),
        buttonText: t('sp.goToSystemsWebPage', { systemName }),
        buttonHref: systemExternalUrl,
        componentData: systemCompareData,
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'serviceRequestFormData',
        componentData: {
          systemImgUrl: `https://ik.imagekit.io/businesswith/tr:w-80,h-40,cm-pad_resize,dpr-2/logo/${logoAsset.url}`,
          systemTitle: systemName,
          guide: {
            imgUrl:
              'https://ik.imagekit.io/businesswith/tr:w-64,h-64,cm-pad_resize,dpr-2/businesswith/peder-muller-v2.jpg',
            title: t('formCommon.header.title', { name: 'Peder' }),
            stepDescriptions: {
              1: t('formCommon.header.subTitleStep1', { systemName }),
              2: t('formCommon.header.subTitleStep2'),
              '2-OFFERREQUEST': t(
                'formCommon.header.subTitleStep2OFFERREQUEST'
              ),
              3: t('formCommon.header.subTitleStep3'),
            },
          },
          urls: {
            legalPrivacyPageUrl,
            legalTermsPageUrl,
          },
          licenses: [
            {
              value: '1-4',
              label: t('formCommon.licensesChoose1'),
            },
            {
              value: '5-10',
              label: t('formCommon.licensesChoose5'),
            },
            {
              value: '11-25',
              label: t('formCommon.licensesChoose11'),
            },
            {
              value: '26-50',
              label: t('formCommon.licensesChoose26'),
            },
            {
              value: '51-100',
              label: t('formCommon.licensesChoose51'),
            },
            {
              value: '100+',
              label: t('formCommon.licensesChoose100'),
            },
            {
              value: 'DONT_KNOW',
              label: t('formCommon.licensesChooseDontKnow'),
            },
          ],
          successMessage: t('formCommon.whatHappensNowText', {
            companyName,
          }),
          handleSubmit: saveInquiry,
          loading: createInquiryLoading,
          saved: createInquiryData?.createInquiry?.result === 'done',
        },
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'writeReviewData',
        header: {
          title: systemName,
          imgUrl: `https://ik.imagekit.io/businesswith/tr:w-80,h-40,cm-pad_resize,dpr-2/logo/${logoAsset.url}`,
        },
        renderBusinesswithReviewForm: ({ closeModalForm }) => (
          <BusinesswithReviewForm
            systemName={systemName}
            systemId={systemId}
            category={category}
            closeModal={closeModalForm}
          />
        ),
      },
    },
  ];

  return {
    customer,
    breadcrumbs,
    sections,
  };

  // const bwSearchData = useTransformGatsbyData(data.bwSearchData);

  // const [searchSuggestions, setSearchSuggestions] = useState([]);
  // const debouncedQuery = useDebouncedValue(searchValue, 200);
  // const { handleDataLayer } = useWithGA();

  // useEffect(() => {
  // }, [debouncedQuery, bwSearchData]);

  // const onSearchClick = (name) => {
  //   document.querySelector('html').classList.remove('scroll-lock');

  //   handleDataLayer({
  //     event_name: 'search',
  //     event_action: `Search result click ${big ? '- large' : '- nav'}`,
  //     search_term: searchValue,
  //     search_result_click: name,
  //   });
  // };

  // return {
  //   searchSuggestions,
  //   onSearchClick,
  // };
};

export default useSystemPage;
